
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.addEventListener('click', this.onDynamicCheckoutClick.bind(this), true);
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      this.customerNameInput = this.form.querySelector('[data-customer-name="true"]');
      this.customerEmailInput = this.form.querySelector('[data-required-email="true"]');
      this.customerContactError = this.querySelector('.product-form__customer-contact-error');
      this.customerEmailApiUrl = this.dataset.customerEmailApiUrl || '/api/official/email';
      this.customerContactSyncPromise = null;
      this.lastSyncedContactKey = '';
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this.hideErrors = this.dataset.hideErrors === 'true';

      if (this.customerNameInput) {
        this.customerNameInput.addEventListener('input', () => {
          this.resetContactSyncIfChanged();
          if (this.customerNameInput.value.trim()) {
            this.customerNameInput.removeAttribute('aria-invalid');
            if (this.customerEmailInput?.value.trim() && this.customerEmailInput.validity.valid) {
              this.clearCustomerContactError();
              this.handleErrorMessage();
            }
          }
        });
      }

      if (this.customerEmailInput) {
        this.customerEmailInput.addEventListener('input', () => {
          this.resetContactSyncIfChanged();
          if (this.customerEmailInput.value.trim() && this.customerEmailInput.validity.valid) {
            this.customerEmailInput.removeAttribute('aria-invalid');
            if (!this.customerNameInput || this.customerNameInput.value.trim()) {
              this.clearCustomerContactError();
              this.handleErrorMessage();
            }
          }
        });

        this.customerEmailInput.addEventListener('blur', () => {
          if (!this.customerEmailInput.value.trim()) return;
          this.validateCustomerContact({ focusOnError: false });
        });
      }

      this.initDynamicCheckoutOverlay();
    }

    connectedCallback() {
      this.initDynamicCheckoutOverlay();
    }

    isCustomerContactFieldsValid() {
      if (!this.customerEmailInput) return true;

      const nameValid = !this.customerNameInput || this.customerNameInput.value.trim() !== '';
      const emailValue = this.customerEmailInput.value.trim();
      const emailValid = emailValue !== '' && this.customerEmailInput.validity.valid;

      return nameValid && emailValid;
    }

    findDynamicCheckoutContainer() {
      // Product page: {{ form | payment_button }} renders .shopify-payment-button
      // Cart page uses #dynamic-checkout-cart — keep as fallback.
      return (
        this.querySelector('.shopify-payment-button') ||
        this.querySelector('#dynamic-checkout-cart') ||
        this.querySelector('.additional-checkout-buttons')
      );
    }

    initDynamicCheckoutOverlay() {
      if (!this.customerEmailInput || !this.customerNameInput) return;
      if (this.dynamicCheckoutOverlay) return;

      const setupIfReady = () => {
        const container = this.findDynamicCheckoutContainer();
        if (!container) return false;

        this.setupDynamicCheckoutOverlay(container);
        return true;
      };

      if (setupIfReady()) return;

      if (this.dynamicCheckoutOverlayObserver) return;

      this.dynamicCheckoutOverlayObserver = new MutationObserver(() => {
        if (setupIfReady()) {
          this.dynamicCheckoutOverlayObserver.disconnect();
          this.dynamicCheckoutOverlayObserver = null;
        }
      });

      this.dynamicCheckoutOverlayObserver.observe(this, { childList: true, subtree: true });

      // Shopify often injects payment buttons slightly after first paint.
      [100, 300, 800, 1500, 3000].forEach((delay) => {
        setTimeout(() => {
          if (this.dynamicCheckoutOverlay) return;
          if (setupIfReady() && this.dynamicCheckoutOverlayObserver) {
            this.dynamicCheckoutOverlayObserver.disconnect();
            this.dynamicCheckoutOverlayObserver = null;
          }
        }, delay);
      });
    }

    setupDynamicCheckoutOverlay(dynamicCheckoutContainer) {
      if (this.dynamicCheckoutOverlay) return;
      if (!dynamicCheckoutContainer) return;

      const nameField = this.customerNameInput;
      const emailField = this.customerEmailInput;

      // Cover the payment button itself (iframes like Shop Pay ignore parent click capture).
      const wrapper = dynamicCheckoutContainer;
      if (window.getComputedStyle(wrapper).position === 'static') {
        wrapper.style.position = 'relative';
      }

      const overlay = document.createElement('div');
      overlay.setAttribute('data-contact-overlay', 'true');
      overlay.setAttribute('aria-hidden', 'true');
      overlay.style.cssText = [
        'position: absolute',
        'inset: 0',
        'width: 100%',
        'height: 100%',
        'z-index: 9999',
        'cursor: pointer',
        'background: transparent',
      ].join(';');

      const blockEvent = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
        this.validateCustomerContact();
      };

      // pointerdown/touchstart catch iframe/wallet buttons before they activate.
      ['pointerdown', 'mousedown', 'touchstart', 'click'].forEach((type) => {
        overlay.addEventListener(type, blockEvent, true);
      });

      wrapper.appendChild(overlay);
      this.dynamicCheckoutOverlay = overlay;
      this.dynamicCheckoutContainer = wrapper;

      const updateOverlay = () => {
        const valid = this.isCustomerContactFieldsValid();
        overlay.style.display = valid ? 'none' : 'block';
        overlay.style.pointerEvents = valid ? 'none' : 'auto';
      };

      nameField.addEventListener('input', updateOverlay);
      emailField.addEventListener('input', updateOverlay);
      updateOverlay();
      this.updateDynamicCheckoutOverlay = updateOverlay;
    }

    getContactPayload() {
      const name = this.customerNameInput ? this.customerNameInput.value.trim() : '';
      const email = this.customerEmailInput ? this.customerEmailInput.value.trim().toLowerCase() : '';
      return { name, email };
    }

    resetContactSyncIfChanged() {
      const { name, email } = this.getContactPayload();
      const contactKey = `${name}|${email}`;
      if (contactKey !== this.lastSyncedContactKey) {
        this.customerContactSyncPromise = null;
      }
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      if (!this.validateCustomerContact()) return;
      if (!(await this.syncCustomerContact())) return;

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }

      const sellingPlanId = window.getCurrentSellingPlanId();
      formData.append('selling_plan', sellingPlanId);

      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {source: 'product-form', productVariantId: formData.get('id'), errors: response.description, message: response.message});
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form', productVariantId: formData.get('id')});
          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    async onDynamicCheckoutClick(evt) {
      const dynamicCheckoutButton = evt.target.closest('.shopify-payment-button__button, .shopify-payment-button [role="button"]');
      if (!dynamicCheckoutButton || !this.contains(dynamicCheckoutButton)) return;

      evt.preventDefault();
      evt.stopImmediatePropagation();
      this.handleErrorMessage();

      if (!this.validateCustomerContact()) return;
      if (!(await this.syncCustomerContact({ force: true }))) return;

      const variantInput = this.form.querySelector('[name="id"]');
      const variantId = variantInput?.value;

      if (!variantId) {
        this.showCustomerContactError('Unable to identify the selected product variant.');
        return;
      }

      const shopRoot = window.Shopify?.routes?.root || '/';
      const { name, email } = this.getContactPayload();
      const cartItem = {
        id: Number(variantId),
        quantity: 1,
        properties: {
          'Clinic/Company Name': name,
          'Account Register Email': email,
        },
      };
      const sellingPlanId = window.getCurrentSellingPlanId?.();
      if (sellingPlanId) cartItem.selling_plan = Number(sellingPlanId);

      dynamicCheckoutButton.setAttribute('aria-disabled', 'true');
      dynamicCheckoutButton.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(`${shopRoot}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items: [cartItem] }),
        });
        const payload = await response.json();

        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || 'Unable to add this product to checkout.');
        }

        window.location.assign(`${shopRoot}checkout`);
      } catch (error) {
        this.showCustomerContactError(error.message || 'Unable to open checkout. Please try again.');
        dynamicCheckoutButton.removeAttribute('aria-disabled');
        dynamicCheckoutButton.removeAttribute('aria-busy');
      }
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }

    showCustomerContactError(message) {
      if (this.customerContactError) {
        this.customerContactError.textContent = message;
        this.customerContactError.hidden = false;
        this.customerContactError.removeAttribute('hidden');
      }
      this.handleErrorMessage(message);
    }

    clearCustomerContactError() {
      if (!this.customerContactError) return;
      this.customerContactError.textContent = '';
      this.customerContactError.hidden = true;
      this.customerContactError.setAttribute('hidden', '');
    }

    validateCustomerContact({ focusOnError = true } = {}) {
      if (!this.customerEmailInput) return true;

      let nameValue = '';
      if (this.customerNameInput) {
        nameValue = this.customerNameInput.value.trim();
        this.customerNameInput.value = nameValue;
      }

      const emailValue = this.customerEmailInput.value.trim().toLowerCase();
      this.customerEmailInput.value = emailValue;

      const nameMissing = Boolean(this.customerNameInput && !nameValue);
      const emailMissing = !emailValue;
      const emailInvalid = !emailMissing && !this.customerEmailInput.validity.valid;

      if (this.customerNameInput) {
        if (nameMissing) {
          this.customerNameInput.setAttribute('aria-invalid', 'true');
        } else {
          this.customerNameInput.removeAttribute('aria-invalid');
        }
      }

      if (emailMissing || emailInvalid) {
        this.customerEmailInput.setAttribute('aria-invalid', 'true');
      } else {
        this.customerEmailInput.removeAttribute('aria-invalid');
      }

      if (nameMissing || emailMissing || emailInvalid) {
        if (nameMissing && emailMissing) {
          this.showCustomerContactError('Clinic/Company Name and Account Register Email are required.');
          if (focusOnError && this.customerNameInput) this.customerNameInput.focus();
          return false;
        }

        if (nameMissing) {
          this.showCustomerContactError('Clinic/Company Name is required.');
          if (focusOnError && this.customerNameInput) this.customerNameInput.focus();
          return false;
        }

        if (emailMissing) {
          this.showCustomerContactError('Account Register Email is required.');
          if (focusOnError) this.customerEmailInput.focus();
          return false;
        }

        this.showCustomerContactError('Please enter a valid account register email.');
        if (focusOnError) this.customerEmailInput.focus();
        return false;
      }

      if (this.customerNameInput) this.customerNameInput.removeAttribute('aria-invalid');
      this.customerEmailInput.removeAttribute('aria-invalid');
      this.clearCustomerContactError();
      return true;
    }

    async syncCustomerContact({ silent = false, force = false } = {}) {
      if (!this.customerEmailInput) return true;

      const { name, email } = this.getContactPayload();
      this.customerEmailInput.value = email;
      if (this.customerNameInput) this.customerNameInput.value = name;

      if (!name || !email) return false;

      const contactKey = `${name}|${email}`;
      if (!force && contactKey === this.lastSyncedContactKey) return true;
      if (this.customerContactSyncPromise) return this.customerContactSyncPromise;

      const requestConfig = fetchConfig();
      requestConfig.headers.Accept = 'application/json';
      requestConfig.headers['Content-Type'] = 'application/json';
      requestConfig.body = JSON.stringify({ name, email });
      if (this.customerEmailApiUrl.indexOf('ngrok') !== -1) {
        requestConfig.headers['ngrok-skip-browser-warning'] = 'true';
      }

      this.customerContactSyncPromise = fetch(this.customerEmailApiUrl, requestConfig)
        .then(async (response) => {
          if (!response.ok) {
            let errorMessage = 'Unable to submit your contact details right now. Please try again.';

            try {
              const payload = await response.json();
              if (payload?.message) errorMessage = payload.message;
            } catch (_error) {
              // Fall back to the default message when the API doesn't return JSON.
            }

            throw new Error(errorMessage);
          }

          this.lastSyncedContactKey = contactKey;
          return true;
        })
        .catch((error) => {
          this.customerContactSyncPromise = null;
          this.lastSyncedContactKey = '';

          if (!silent) {
            this.customerEmailInput.setAttribute('aria-invalid', 'true');
            this.showCustomerContactError(error.message || 'Unable to submit your contact details right now. Please try again.');
            this.customerEmailInput.focus();
          }

          return false;
        });

      const syncResult = await this.customerContactSyncPromise;
      if (syncResult) this.customerContactSyncPromise = null;
      return syncResult;
    }
  });
}
